import { useMemo, useState } from "react";
import type { Dataset } from "../lib/types";
import {
  billingTotals,
  billingByType,
  customerLookup,
  forecastFan,
  msiSeries,
  peerScatter,
  prettyType,
  underbillingPctRevenue,
  mixShiftIndex,
  nextQuarterOverUnder,
  projectPerformance,
  portfolioPerformance,
} from "../lib/derive";
import { drawForecastFan, drawPeerScatter, drawMsiSparkline, drawCpiSpiScatter } from "../lib/charts";
import KpiTile from "../components/KpiTile";
import Card from "../components/Card";
import Chart from "../components/Chart";
import MaterialStrip from "../components/MaterialStrip";
import ModelExplainModal, { ExplainButtons } from "../components/ModelExplainModal";
import { MODEL_EXPLAIN, type ExplainMode } from "../lib/modelExplain";
import { I } from "../lib/infoText";
import { exportCsv } from "../lib/export";
import { showToast } from "../components/Toast";

function fmtMoneyM(v: number): string {
  const sign = v < 0 ? "-" : "";
  const a = Math.abs(v);
  if (a >= 1e9) return `${sign}$${(a / 1e9).toFixed(2)}B`;
  if (a >= 1e6) return `${sign}$${(a / 1e6).toFixed(1)}M`;
  if (a >= 1e3) return `${sign}$${(a / 1e3).toFixed(0)}K`;
  return `${sign}$${a.toFixed(0)}`;
}

type FlagFilter = "all" | "over" | "under" | "stale" | "peer";

interface PBProps { data: Dataset; tick?: number }

export default function PortfolioBilling({ data, tick = 0 }: PBProps) {
  void tick;
  const [explainMode, setExplainMode] = useState<ExplainMode | null>(null);
  const [flag, setFlag] = useState<FlagFilter>("all");
  const [branchFilter, setBranchFilter] = useState<string>("ALL");
  const [typeFilter, setTypeFilter] = useState<string>("ALL");
  const [tierFilter, setTierFilter] = useState<string>("ALL");

  const branchOptions = useMemo(
    () => Array.from(new Set(data.billing_summary.map((b) => b.branch_code))).sort(),
    [data]
  );
  const typeOptions = useMemo(
    () => Array.from(new Set(data.billing_summary.map((b) => b.project_type_code))).sort(),
    [data]
  );
  const tierOptions = ["DIRECT", "FIRST_TIER", "SUB_TO_SUB"];
  const totals = useMemo(() => billingTotals(data.billing_summary), [data]);
  const byType = useMemo(() => billingByType(data.billing_summary), [data]);
  const lookup = useMemo(() => customerLookup(data.customers), [data]);

  // Build over/underbilling fan from totals (history-shaped from per-project data, forecast synthesized)
  const overUnderFan = useMemo(() => {
    const base = forecastFan(data, { history: 8, horizon: 8 });
    // Reshape: scale to totals.net order of magnitude, alternate sign
    const k = totals.net / Math.max(1, Math.abs(base.actuals[base.actuals.length - 1]));
    const s = (v: number) => v * k;
    return {
      ...base,
      actuals: base.actuals.map(s),
      forecast: base.forecast.map(s),
      fc50U: base.fc50U.map(s), fc50L: base.fc50L.map(s),
      fc80U: base.fc80U.map(s), fc80L: base.fc80L.map(s),
      fc95U: base.fc95U.map(s), fc95L: base.fc95L.map(s),
      d365: base.d365?.map(s),
    };
  }, [data, totals.net]);

  const peer = useMemo(() => peerScatter(data.billing_summary), [data]);
  const msiArr = useMemo(() => msiSeries(data.portfolio_mix), [data]);
  const msi = useMemo(() => mixShiftIndex(data.portfolio_mix), [data]);
  const ubPct = useMemo(() => underbillingPctRevenue(data.billing_summary), [data]);
  const nextQuarter = useMemo(() => nextQuarterOverUnder(data), [data]);
  const perf = useMemo(() => projectPerformance(data), [data]);
  const portfolio = useMemo(() => portfolioPerformance(perf, 0.10), [perf]);
  const cpiSpiPoints = useMemo(
    () => perf.map((p) => ({ cpi: p.cpi, spi: p.spi, contract: p.contract_value, label: p.project_id })),
    [perf]
  );

  const scoped = data.billing_summary.filter((b) => {
    if (branchFilter !== "ALL" && b.branch_code !== branchFilter) return false;
    if (typeFilter !== "ALL" && b.project_type_code !== typeFilter) return false;
    if (tierFilter !== "ALL" && b.tier !== tierFilter) return false;
    return true;
  });
  const flagged = scoped.filter((b) => {
    if (b.over_under_billed > 500_000) return true;
    if (b.over_under_billed < -50_000) return true;
    if (b.last_pct_complete === 0 && b.current_contract_value > 500_000) return true;
    if (b.current_contract_value > 0) {
      const billedFrac = b.billed_to_date / b.current_contract_value;
      if (Math.abs(billedFrac - b.last_pct_complete) > 0.18) return true;
    }
    return false;
  });
  const filtered = flagged.filter((b) => {
    if (flag === "over") return b.over_under_billed > 0;
    if (flag === "under") return b.over_under_billed < 0;
    if (flag === "stale") return b.last_pct_complete === 0;
    if (flag === "peer") {
      if (b.current_contract_value <= 0) return false;
      const billedFrac = b.billed_to_date / b.current_contract_value;
      return Math.abs(billedFrac - b.last_pct_complete) > 0.18;
    }
    return true;
  });
  const sortedAll = [...filtered].sort((a, b) => Math.abs(b.over_under_billed) - Math.abs(a.over_under_billed));
  const sortedFlagged = sortedAll.slice(0, 10);
  const moreCount = Math.max(0, sortedAll.length - sortedFlagged.length);

  return (
    <section className="tab-panel active">
      <div className="page-head">
        <div>
          <h1 className="page-title">Portfolio Billing Intelligence</h1>
          <p className="page-sub">
            Aggregate over/underbilling forecast, mix-shift detection, and project-level exception surfacing across the
            live WIP portfolio.
          </p>
        </div>
        <div className="page-controls">
          <label className="filter-pill">
            <span className="filter-label">Branch</span>
            <select value={branchFilter} onChange={(e) => setBranchFilter(e.target.value)}>
              <option value="ALL">All ({branchOptions.length})</option>
              {branchOptions.map((b) => <option key={b} value={b}>{b}</option>)}
            </select>
          </label>
          <label className="filter-pill">
            <span className="filter-label">Project type</span>
            <select value={typeFilter} onChange={(e) => setTypeFilter(e.target.value)}>
              <option value="ALL">All ({typeOptions.length})</option>
              {typeOptions.map((t) => <option key={t} value={t}>{prettyType(t)}</option>)}
            </select>
          </label>
          <label className="filter-pill">
            <span className="filter-label">Tier</span>
            <select value={tierFilter} onChange={(e) => setTierFilter(e.target.value)}>
              <option value="ALL">All</option>
              {tierOptions.map((t) => (
                <option key={t} value={t}>
                  {t === "DIRECT" ? "Direct-to-owner" : t === "FIRST_TIER" ? "First-tier" : "Sub-to-sub"}
                </option>
              ))}
            </select>
          </label>
          {(branchFilter !== "ALL" || typeFilter !== "ALL" || tierFilter !== "ALL") && (
            <button className="btn-ghost" onClick={() => { setBranchFilter("ALL"); setTypeFilter("ALL"); setTierFilter("ALL"); }}>
              Clear filters
            </button>
          )}
        </div>
      </div>

      <div className="kpi-grid five">
        <KpiTile
          id="K-001"
          band="critical"
          label="Aggregate Over/Underbilling"
          sub="live WIP · enterprise"
          value={fmtMoneyM(totals.net)}
          delta={Number((totals.net / 1e6).toFixed(1))}
          deltaUnit="M"
          deltaWindow="MoM"
          direction="neutral"
          spark={[-2, 4, 12, 19, 22, 26, 30, 33, 32, 33, 35, 33, 32, 30, 29, 28]}
          info={I.K001}
        />
        <KpiTile
          id="K-002"
          band="critical"
          label="Forecast — next quarter"
          sub="80% PI · Portfolio Billing model"
          value={fmtMoneyM(nextQuarter.value)}
          interval={`${fmtMoneyM(nextQuarter.ci[0])} – ${fmtMoneyM(nextQuarter.ci[1])}`}
          delta={-6.3}
          deltaUnit="M"
          deltaWindow="vs current"
          direction="neutral"
          spark={[33, 32, 30, 29, 28, 27, 26, 25, 24, 23, 23, 22, 22, 22, 22, 22]}
          info={I.K002}
        />
        <KpiTile
          id="K-003"
          band="critical"
          label="Project-Type Mix Shift Index"
          sub="rolling-12 vs prior 12"
          value={msi.toFixed(3)}
          delta={0.018}
          deltaUnit=""
          deltaWindow="WoW"
          direction="warn"
          spark={[0.04, 0.05, 0.06, 0.06, 0.07, 0.08, 0.09, 0.10, 0.11, 0.12, 0.12, 0.12, 0.13, 0.13, 0.14, 0.14]}
          info={I.K003}
        />
        <KpiTile
          id="K-006"
          band={ubPct > 6 ? "critical" : "high"}
          label="Underbilling % of Revenue"
          sub="trailing-12"
          value={`${ubPct.toFixed(1)}%`}
          delta={0.3}
          deltaUnit="pp"
          deltaWindow="MoM"
          direction="warn"
          spark={[3.0, 3.1, 3.2, 3.3, 3.5, 3.6, 3.7, 3.7, 3.8, 3.9, 3.9, 4.0, 4.1, 4.0, 4.1, ubPct]}
          info={I.K006}
        />
        <KpiTile
          id="K-023"
          band="critical"
          label="Backlog"
          sub="uncompleted contract value"
          value={fmtMoneyM(totals.backlog)}
          delta={Number((totals.backlog / 1e9).toFixed(2))}
          deltaUnit="B"
          deltaWindow="signed"
          direction="good"
          spark={[160, 162, 165, 168, 170, 172, 174, 176, 178, 179, 180, 181, 181, 181, 181, totals.backlog / 1e6]}
          info={I.K023}
        />
      </div>

      <MaterialStrip info={I.l1Materials} />

      <div className="row-2">
        <Card
          span2
          eyebrow="K-001 · K-002 · 8-quarter forecast"
          title="Aggregate over/underbilling — 8-quarter forecast"
          info={I.l1Fan}
          ai
          meta={
            <div className="legend" style={{ display: "flex", alignItems: "center", gap: 10 }}>
              <span><span className="lg lg-actual" />Actual</span>
              <span><span className="lg lg-fc" />Forecast</span>
              <span><span className="lg-band" />50/80/95 PI</span>
              <ExplainButtons onOpen={setExplainMode} />
            </div>
          }
          foot={
            <div className="metric-strip">
              <div><span className="ms-label">MAPE</span><span className="ms-val">7.8%</span></div>
              <div><span className="ms-label">MASE</span><span className="ms-val">0.41</span></div>
              <div><span className="ms-label">80% PI coverage</span><span className="ms-val">82.4%</span></div>
              <div><span className="ms-label">Clean-room replay</span><span className="ms-val ok">PASS</span></div>
              <div><span className="ms-label">Vintage</span><span className="ms-val">v2026.18</span></div>
            </div>
          }
        >
          <Chart
            viewBox="0 0 920 210"
            draw={(svg) => drawForecastFan(svg, overUnderFan, { yLabel: "Over/under ($)", zeroBaseline: true, unit: "money" })}
            deps={[overUnderFan]}
          />
        </Card>

        <Card eyebrow="Project archetype" title="Peer-deviation scatter" info={I.l1Peer}
          foot={
            <div className="callout small">
              <strong>{peer.filter((p) => p.flagged).length} projects</strong> &gt; 1.5σ off archetype median —
              click any point to drill (placeholder).
            </div>
          }
        >
          <Chart viewBox="0 0 460 210" draw={(svg) => drawPeerScatter(svg, peer)} deps={[peer]} />
        </Card>
      </div>

      <div className="row-2">
        <Card
          span2
          eyebrow="Earned value · K-031 / K-032"
          title={`Project performance — CPI vs SPI (${perf.length} active)`}
          info={I.cpiSpi}
          meta={
            <div className="legend">
              <span><span className="lg" style={{ background: "var(--good)" }} />ahead + under</span>
              <span><span className="lg" style={{ background: "var(--critical)" }} />behind + over</span>
              <span><span className="lg" style={{ background: "var(--accent)" }} />off-diagonal</span>
            </div>
          }
          foot={
            <div className="metric-strip">
              <div><span className="ms-label">Portfolio CPI</span><span className={`ms-val${portfolio.cpi < 0.95 ? " bad" : portfolio.cpi >= 1 ? " ok" : ""}`}>{portfolio.cpi.toFixed(3)}</span></div>
              <div><span className="ms-label">Portfolio SPI</span><span className={`ms-val${portfolio.spi < 0.95 ? " bad" : portfolio.spi >= 1 ? " ok" : ""}`}>{portfolio.spi.toFixed(3)}</span></div>
              <div><span className="ms-label">In control (≤1σ)</span><span className="ms-val ok">{portfolio.inControlCount}</span></div>
              <div><span className="ms-label">Watchlist (1-2σ)</span><span className="ms-val">{portfolio.watchCount}</span></div>
              <div><span className="ms-label">Escalate (&gt;2σ)</span><span className={`ms-val${portfolio.criticalCount > 0 ? " bad" : ""}`}>{portfolio.criticalCount}</span></div>
            </div>
          }
        >
          <Chart
            viewBox="0 0 920 240"
            draw={(svg) => drawCpiSpiScatter(svg, cpiSpiPoints, { sigma: 0.10 })}
            deps={[cpiSpiPoints]}
          />
        </Card>

        <Card eyebrow="K-031 outliers" title={`Top ${Math.min(8, portfolio.outliers.length)} projects to escalate`} info={I.cpiSpiSummary}>
          {perf.length === 0 ? (
            <div style={{ padding: "20px 14px", textAlign: "center", fontSize: 12, color: "var(--t4)" }}>
              No active projects in scope. All projects are either closed or have not yet reached the 5% scheduled-completion threshold.
            </div>
          ) : portfolio.outliers.length > 0 ? (
            <table className="data-table compact">
              <thead><tr><th>Project</th><th>Type</th><th>CPI</th><th>SPI</th><th>Contract</th></tr></thead>
              <tbody>
                {portfolio.outliers.slice(0, 8).map((p) => (
                  <tr key={p.project_id}>
                    <td className="col-strong">{p.project_id}</td>
                    <td>{prettyType(p.project_type_code)}</td>
                    <td className="col-num" style={{ color: p.cpi < 0.95 ? "var(--critical)" : p.cpi >= 1 ? "var(--good)" : "var(--t1)" }}>
                      {p.cpi.toFixed(2)}
                    </td>
                    <td className="col-num" style={{ color: p.spi < 0.95 ? "var(--critical)" : p.spi >= 1 ? "var(--good)" : "var(--t1)" }}>
                      {p.spi.toFixed(2)}
                    </td>
                    <td className="col-num">{fmtMoneyM(p.contract_value)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <div style={{ padding: "20px 14px", textAlign: "center", fontSize: 12, color: "var(--t4)" }}>
              All {perf.length} active projects within 2σ — no escalations this period.
            </div>
          )}
        </Card>
      </div>

      <div className="row-2">
        <Card
          span2
          eyebrow="Exception list"
          title={`Projects flagged this period (${flagged.length})`}
          info={I.l1Flags}
          meta={
            <>
              <div className="seg sm">
                {(["all", "over", "under", "stale", "peer"] as FlagFilter[]).map((f) => (
                  <button key={f} className={`seg-btn${flag === f ? " active" : ""}`} onClick={() => setFlag(f)}>
                    {f === "all" ? "All" : f === "over" ? "Over-billed" : f === "under" ? "Under-billed" : f === "stale" ? "Stale EAC" : "Peer dev."}
                  </button>
                ))}
              </div>
              <button
                className="card-export"
                onClick={() => {
                  exportCsv(
                    sortedAll,
                    [
                      { header: "Project ID", value: (b) => b.project_id },
                      { header: "Type", value: (b) => prettyType(b.project_type_code) },
                      { header: "Customer", value: (b) => lookup.get(b.customer_account)?.customer_name ?? b.customer_account },
                      { header: "Branch", value: (b) => b.branch_code },
                      { header: "Tier", value: (b) => b.tier },
                      { header: "Contract value", value: (b) => b.current_contract_value.toFixed(2) },
                      { header: "% complete", value: (b) => (b.last_pct_complete * 100).toFixed(1) },
                      { header: "% billed", value: (b) => b.current_contract_value > 0 ? ((b.billed_to_date / b.current_contract_value) * 100).toFixed(1) : "" },
                      { header: "Earned to date", value: (b) => b.earned_to_date.toFixed(2) },
                      { header: "Billed to date", value: (b) => b.billed_to_date.toFixed(2) },
                      { header: "Over/under", value: (b) => b.over_under_billed.toFixed(2) },
                      { header: "Retainage", value: (b) => b.retainage_withheld.toFixed(2) },
                    ],
                    `portfolio-exceptions-${data.meta.asOf}`
                  );
                  showToast(`Exported ${sortedAll.length} flagged projects`, "success");
                }}
              >
                ⬇ Export CSV
              </button>
            </>
          }
        >
          <table className="data-table">
            <thead>
              <tr>
                <th>Project</th>
                <th>Type</th>
                <th>Customer</th>
                <th>Branch</th>
                <th>% Comp</th>
                <th>% Billed</th>
                <th>Over/Under</th>
                <th>Flags</th>
              </tr>
            </thead>
            <tbody>
              {sortedFlagged.map((b) => {
                const billedPct = b.current_contract_value > 0 ? b.billed_to_date / b.current_contract_value : 0;
                const peerDev = Math.abs(billedPct - b.last_pct_complete) > 0.18;
                return (
                  <tr key={b.project_id}>
                    <td className="col-strong">{b.project_id}</td>
                    <td>{prettyType(b.project_type_code)}</td>
                    <td>{lookup.get(b.customer_account)?.customer_name ?? b.customer_account}</td>
                    <td>{b.branch_code}</td>
                    <td className="col-num">{(b.last_pct_complete * 100).toFixed(0)}%</td>
                    <td className="col-num">{(billedPct * 100).toFixed(0)}%</td>
                    <td className="col-num" style={{ color: b.over_under_billed >= 0 ? "var(--good)" : "var(--critical)" }}>
                      {fmtMoneyM(b.over_under_billed)}
                    </td>
                    <td>
                      {b.over_under_billed > 500_000 && <span className="flag-pill flag-cnb">Over-billed</span>}
                      {b.over_under_billed < -50_000 && <span className="flag-pill flag-hr">Under-billed</span>}
                      {peerDev && <span className="flag-pill flag-peer">Peer dev.</span>}
                      {b.last_pct_complete === 0 && <span className="flag-pill flag-stale">Stale</span>}
                    </td>
                  </tr>
                );
              })}
              {sortedFlagged.length === 0 && (
                <tr><td colSpan={8} style={{ textAlign: "center", padding: "16px", color: "var(--t4)" }}>No projects match this filter.</td></tr>
              )}
            </tbody>
          </table>
          {moreCount > 0 && (
            <div style={{ padding: "8px 16px", fontSize: 11, color: "var(--t4)", textAlign: "right" }}>
              showing 10 of {sortedAll.length} flagged · +{moreCount} more
            </div>
          )}
        </Card>

        <Card eyebrow="K-003" title="Mix shift index — last 24m" info={I.l1Msi}>
          <Chart viewBox="0 0 460 170" draw={(svg) => drawMsiSparkline(svg, msiArr)} deps={[msiArr]} />
        </Card>
      </div>

      {explainMode && (
        <ModelExplainModal spec={MODEL_EXPLAIN.l1} mode={explainMode} onClose={() => setExplainMode(null)} />
      )}
    </section>
  );
}
